/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useDispatch } from 'react-redux';
import { isMobile, isDesktop, isTablet } from 'react-device-detect';

import Heading from '../../components/heading/Heading';
import Item from '../../components/custom/Item';
import CustomNoRowsOlverlay from '../../components/custom/CustomNoRowsOlverlay';
import msgNotificacion from '../../scripts/notification';
import Formatter from '../../scripts/formatters';
import ClientesService from '../../services/clientesService';
import { showProgress } from '../../redux/reducers/progress.Slice';
import { PAGE_SIZE, NAME_PROJECT } from '../../redux/constants/index';
import '../../styles/clientes.css';

export default function GeolocalizacionClientes() {
  const dispatch = useDispatch();
  const userLogin = JSON.parse(localStorage.getItem('user'));
  const esAdminRrhh = [1, 2].includes(Number(userLogin?.CodigoRol));
  const isMobileView = useMediaQuery('(max-width: 740px)');

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [misVendedores, setMisVendedores] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState(null);
  const [vendedorFiltro, setVendedorFiltro] = useState(null);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [modalGeo, setModalGeo] = useState({ open: false, cliente: null });
  const [modalHistorial, setModalHistorial] = useState({ open: false, cliente: null, rows: [] });
  const [latitud, setLatitud] = useState(null);
  const [longitud, setLongitud] = useState(null);
  const [observacion, setObservacion] = useState('');

  const opcionesVendedor = useMemo(
    () =>
      (vendedores || []).map((v) => ({
        id: v.CodVendedor,
        label: `${v.CodVendedor} — ${v.NombreVendedor}`,
      })),
    [vendedores]
  );

  const opcionesMisVendedores = useMemo(
    () =>
      (misVendedores || []).map((v) => ({
        id: v.CodVendedor,
        label: `${v.CodVendedor} — ${v.NombreVendedor}`,
      })),
    [misVendedores]
  );

  const mostrarSelectorVendedor = esAdminRrhh || misVendedores.length > 1;
  const opcionesSelector = esAdminRrhh ? opcionesVendedor : opcionesMisVendedores;
  const valorSelector = esAdminRrhh ? vendedorFiltro : vendedorSeleccionado;
  const codVendedorActivo = valorSelector?.id || null;

  const filasFiltradas = useMemo(() => {
    const q = filtroTexto.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.CodCliente || '').toLowerCase().includes(q) ||
        String(r.NOMBRE_CLIENTE || '').toLowerCase().includes(q)
    );
  }, [rows, filtroTexto]);

  const vendedorActivo = useMemo(() => {
    if (!valorSelector) return null;
    const partes = String(valorSelector.label || '').split(' — ');
    return {
      CodVendedor: valorSelector.id,
      NombreVendedor: partes.slice(1).join(' — ') || '',
    };
  }, [valorSelector]);

  const cargarMisVendedores = async () => {
    const response = await ClientesService.getMiVendedor({
      idUsuario: userLogin.CodigoUsuario,
    });
    if (response?.Codigo === 1 && response.Data?.length) {
      setMisVendedores(response.Data);
      if (response.Data.length === 1) {
        const v = response.Data[0];
        const opcion = {
          id: v.CodVendedor,
          label: `${v.CodVendedor} — ${v.NombreVendedor}`,
        };
        setVendedorSeleccionado(opcion);
        return { cod: v.CodVendedor, total: 1 };
      }
      return { cod: null, total: response.Data.length };
    }
    setMisVendedores([]);
    setVendedorSeleccionado(null);
    return { cod: null, total: 0 };
  };

  const cargarClientes = async (codVendedorOverride = null) => {
    const codVendedor = codVendedorOverride || codVendedorActivo;
    if (!codVendedor) {
      setRows([]);
      if (!esAdminRrhh) {
        msgNotificacion(
          'Seleccione el código de vendedor o contacte a RRHH si no tiene asociación.',
          'warning',
          4000
        );
      }
      return;
    }

    try {
      setLoading(true);
      dispatch(showProgress(true));

      const payload = {
        idUsuario: userLogin.CodigoUsuario,
        codigoRol: userLogin.CodigoRol,
        codVendedor,
      };

      const response = await ClientesService.getClientesVendedor(payload);

      if (response?.Codigo === 1) {
        setRows(response.Data || []);
      } else {
        setRows([]);
        msgNotificacion(response?.Mensaje || 'No se pudieron cargar clientes.', 'warning');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      dispatch(showProgress(false));
    }
  };

  const inicializar = async () => {
    if (esAdminRrhh) {
      const resVend = await ClientesService.getVendedores();
      if (resVend?.Codigo === 1) setVendedores(resVend.Data || []);
    }

    const { cod, total } = await cargarMisVendedores();
    if (cod) {
      await cargarClientes(cod);
    } else if (!esAdminRrhh && total === 0) {
      msgNotificacion(
        'Su usuario no tiene vendedor asociado. Contacte a RRHH.',
        'warning',
        4000
      );
    }
  };

  useEffect(() => {
    document.title = `${NAME_PROJECT} | Geolocalización clientes`;
    inicializar();
  }, []);

  const obtenerUbicacion = () => {
    if (!navigator.geolocation) {
      msgNotificacion('La geolocalización no es compatible con este navegador.', 'error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitud(position.coords.latitude);
        setLongitud(position.coords.longitude);
        msgNotificacion('Ubicación obtenida correctamente.', 'success');
      },
      (error) => {
        msgNotificacion(error.message || 'No se pudo obtener la ubicación.', 'error');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const abrirCaptura = (cliente) => {
    setModalGeo({ open: true, cliente });
    setLatitud(cliente.Latitud ? Number(cliente.Latitud) : null);
    setLongitud(cliente.Longitud ? Number(cliente.Longitud) : null);
    setObservacion('');
  };

  const guardarGeolocalizacion = async () => {
    if (!modalGeo.cliente) return;
    if (latitud == null || longitud == null) {
      msgNotificacion('Obtenga la ubicación antes de guardar.', 'warning');
      return;
    }

    try {
      dispatch(showProgress(true));
      const response = await ClientesService.guardarGeolocalizacion({
        idUsuario: userLogin.CodigoUsuario,
        codigoRol: userLogin.CodigoRol,
        codVendedor: codVendedorActivo,
        codCliente: modalGeo.cliente.CodCliente,
        latitud,
        longitud,
        dispositivo: isMobile ? 'Móvil' : isTablet ? 'Tablet' : isDesktop ? 'Computadora' : 'Otro',
        observacion,
      });

      if (response?.Codigo === 1) {
        msgNotificacion(response.Mensaje, 'success');
        setModalGeo({ open: false, cliente: null });
        cargarClientes(codVendedorActivo);
      } else {
        msgNotificacion(response?.Mensaje || 'No se pudo guardar.', 'warning');
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(showProgress(false));
    }
  };

  const verHistorial = async (cliente) => {
    try {
      dispatch(showProgress(true));
      const response = await ClientesService.getHistorialGeolocalizacion({
        codCliente: cliente.CodCliente,
      });
      setModalHistorial({
        open: true,
        cliente,
        rows: response?.Codigo === 1 ? response.Data || [] : [],
      });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(showProgress(false));
    }
  };

  const abrirMaps = (lat, lng) => {
    if (!lat || !lng) {
      msgNotificacion('Sin coordenadas registradas.', 'warning');
      return;
    }
    window.open(`https://maps.google.com/?q=${lat},${lng}`, '_blank');
  };

  const renderAccionesCliente = (row) => (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      <Tooltip title="Capturar / actualizar ubicación">
        <IconButton size="small" color="primary" onClick={() => abrirCaptura(row)}>
          <MyLocationIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Ver en mapa">
        <IconButton
          size="small"
          color="success"
          onClick={() => abrirMaps(row.Latitud, row.Longitud)}
        >
          <LocationOnIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Historial">
        <IconButton size="small" color="secondary" onClick={() => verHistorial(row)}>
          <HistoryIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderTarjetaCliente = (row) => (
    <Card key={row.CodCliente} className="cliente-geo-card">
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={700}>
            {row.CodCliente}
          </Typography>
          <Typography variant="body2">{row.NOMBRE_CLIENTE}</Typography>
          {Number(row.TieneGeolocalizacion) === 1 ? (
            <Chip size="small" color="success" label="Geolocalizado" />
          ) : (
            <Chip size="small" color="default" label="Pendiente" />
          )}
          {row.Latitud && row.Longitud && (
            <Typography variant="caption" color="text.secondary">
              {row.Latitud}, {row.Longitud}
            </Typography>
          )}
          {row.FechaGeolocalizacion && (
            <Typography variant="caption" color="text.secondary">
              Última captura: {Formatter.dateTimeFormatter(row.FechaGeolocalizacion)}
            </Typography>
          )}
          <Stack direction="column" spacing={1} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<MyLocationIcon />}
              onClick={() => abrirCaptura(row)}
              fullWidth
            >
              Capturar ubicación
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<LocationOnIcon />}
                onClick={() => abrirMaps(row.Latitud, row.Longitud)}
                fullWidth
                disabled={!row.Latitud || !row.Longitud}
              >
                Ver mapa
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<HistoryIcon />}
                onClick={() => verHistorial(row)}
                fullWidth
              >
                Historial
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  const columns = [
    { field: 'CodCliente', headerName: 'Código', width: 110 },
    { field: 'NOMBRE_CLIENTE', headerName: 'Cliente', minWidth: 220, flex: 1 },
    {
      field: 'TieneGeolocalizacion',
      headerName: 'Estado',
      width: 130,
      renderCell: ({ value }) =>
        Number(value) === 1 ? (
          <Chip size="small" color="success" label="Geolocalizado" />
        ) : (
          <Chip size="small" color="default" label="Pendiente" />
        ),
    },
    { field: 'Latitud', headerName: 'Latitud', width: 110 },
    { field: 'Longitud', headerName: 'Longitud', width: 110 },
    {
      field: 'FechaGeolocalizacion',
      headerName: 'Última captura',
      width: 160,
      valueFormatter: (value) => (value ? Formatter.dateTimeFormatter(value) : '—'),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => renderAccionesCliente(row),
    },
  ];

  return (
    <Grid container spacing={1} paddingX={1} paddingTop={1}>
      <Grid item xs={12}>
        <Heading title="Geolocalización de clientes">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() =>
              cargarClientes(codVendedorActivo)
            }
          >
            Actualizar
          </Button>
        </Heading>
      </Grid>

      <Grid item xs={12}>
        <Item>
          <Stack spacing={2} sx={{ mb: 2 }}>
            {vendedorActivo && (
              <Typography variant="body2" color="text.secondary">
                Vendedor: <strong>{vendedorActivo.CodVendedor}</strong>
                {vendedorActivo.NombreVendedor ? ` — ${vendedorActivo.NombreVendedor}` : ''}
              </Typography>
            )}
            {esAdminRrhh && !vendedorActivo && (
              <Typography variant="body2" color="text.secondary">
                Seleccione un vendedor para ver sus clientes.
              </Typography>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {mostrarSelectorVendedor && (
                <Autocomplete
                  sx={{ minWidth: 280 }}
                  options={opcionesSelector}
                  value={valorSelector}
                  onChange={(_, value) => {
                    if (esAdminRrhh) {
                      setVendedorFiltro(value);
                    } else {
                      setVendedorSeleccionado(value);
                    }
                    if (value?.id) cargarClientes(value.id);
                    else setRows([]);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={esAdminRrhh ? 'Seleccionar vendedor' : 'Mi código de vendedor'}
                      size="small"
                    />
                  )}
                />
              )}
              <TextField
                size="small"
                label="Buscar cliente"
                value={filtroTexto}
                onChange={(e) => setFiltroTexto(e.target.value)}
                sx={{ minWidth: 240 }}
              />
            </Stack>
          </Stack>

          {isMobileView ? (
            <Box className="clientes-geo-mobile-list">
              {loading ? (
                <Stack alignItems="center" py={4}>
                  <CircularProgress />
                </Stack>
              ) : filasFiltradas.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                  Sin registros
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {filasFiltradas.map((row) => renderTarjetaCliente(row))}
                </Stack>
              )}
            </Box>
          ) : (
            <DataGrid
              rows={filasFiltradas}
              columns={columns}
              getRowId={(row) => row.CodCliente}
              loading={loading}
              pageSizeOptions={[PAGE_SIZE, 25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: PAGE_SIZE } } }}
              slots={{ noRowsOverlay: CustomNoRowsOlverlay }}
              disableRowSelectionOnClick
              className="MuiPaper-root-dataGrid-clientes"
            />
          )}
        </Item>
      </Grid>

      <Dialog
        open={modalGeo.open}
        onClose={() => setModalGeo({ open: false, cliente: null })}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Capturar ubicación</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>{modalGeo.cliente?.CodCliente}</strong> — {modalGeo.cliente?.NOMBRE_CLIENTE}
            </Typography>
            <Button variant="outlined" startIcon={<MyLocationIcon />} onClick={obtenerUbicacion}>
              Obtener ubicación actual
            </Button>
            <TextField
              label="Latitud"
              value={latitud ?? ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Longitud"
              value={longitud ?? ''}
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              label="Observación (opcional)"
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalGeo({ open: false, cliente: null })}>Cancelar</Button>
          <Button variant="contained" onClick={guardarGeolocalizacion}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modalHistorial.open}
        onClose={() => setModalHistorial({ open: false, cliente: null, rows: [] })}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Historial — {modalHistorial.cliente?.CodCliente} {modalHistorial.cliente?.NOMBRE_CLIENTE}
        </DialogTitle>
        <DialogContent dividers>
          {modalHistorial.rows.length === 0 ? (
            <Typography variant="body2">Sin registros de geolocalización.</Typography>
          ) : (
            modalHistorial.rows.map((item) => (
              <Stack
                key={item.IdGeolocalizacion}
                direction="row"
                justifyContent="space-between"
                sx={{ py: 1, borderBottom: '1px solid #eee' }}
              >
                <Typography variant="body2">
                  {Formatter.dateTimeFormatter(item.FechaRegistro)} — {item.Latitud}, {item.Longitud}
                  {item.UsuarioRegistro ? ` (${item.UsuarioRegistro})` : ''}
                </Typography>
                <IconButton size="small" onClick={() => abrirMaps(item.Latitud, item.Longitud)}>
                  <LocationOnIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalHistorial({ open: false, cliente: null, rows: [] })}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
